import { EditorState, Modifier, SelectionState } from 'draft-js';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getSelectedEntity } from '../../utils';

import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const rootCss = ({ spacing, zIndex }) => ({
  zIndex: `${zIndex.modal + 2} !important`,
  '.TE-popover': {
    minWidth: 300,
    padding: spacing(2),
    textAlign: 'center',
  },
  '.TE-textField': { display: 'block' },
});

const TOOLTIP = 'TOOLTIP';

export default function TooltipEditor({ anchorEl, editorState, onClose, onExited, onUpdateEditorState, open }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');

  useEffect(() => {
    if (open) {
      let selection = editorState.getSelection();
      const origSelection = selection;
      const startKey = selection.getStartKey();
      const contentState = editorState.getCurrentContent();
      const block = contentState.getBlockForKey(startKey);
      const blockText = block.getText();

      // If multiple blocks are selected, change selection to only the initial block
      if (startKey !== selection.getEndKey()) {
        selection = new SelectionState({
          anchorKey: selection.getStartKey(),
          anchorOffset: selection.getStartOffset(),
          focusKey: selection.getStartKey(),
          focusOffset: blockText.length,
        });
      }

      const selectedEntity = getSelectedEntity(editorState);
      if (selectedEntity?.entity?.type === TOOLTIP) {
        const { entity, key } = selectedEntity;
        const { title } = entity.getData();
        setTitle(title);
        const list = block.getCharacterList();
        selection = selection.merge({
          anchorOffset: list.findIndex(c => c.getEntity() === key),
          focusOffset: list.findLastIndex(c => c.getEntity() === key) + 1,
        });
      }
      if (selection !== origSelection) {
        onUpdateEditorState(EditorState.acceptSelection(editorState, selection));
      }
      const text = blockText.substring(selection.getStartOffset(), selection.getEndOffset());
      setText(text);
    }
  }, [editorState, onUpdateEditorState, open]);

  const textInputRef = useRef();
  useLayoutEffect(() => {
    if (open) textInputRef.current?.focus();
  }, [text, open]);

  function confirmLink() {
    let contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    let entityKey;
    const selectedEntity = getSelectedEntity(editorState);
    if (selectedEntity?.entity?.type === TOOLTIP) {
      const { key: existingKey } = selectedEntity;
      contentState = contentState.mergeEntityData(existingKey, { title });
      entityKey = existingKey;
    } else {
      contentState = contentState.createEntity(TOOLTIP, 'MUTABLE', { title });
      entityKey = contentState.getLastCreatedEntityKey();
    }

    contentState = Modifier.replaceText(contentState, selection, text, null, entityKey);

    const newSelOffset = selection.getStartOffset() + (text || '').length;

    const editorStateWithLinkText = EditorState.set(editorState, {
      currentContent: contentState,
      selection: selection.merge({ focusOffset: newSelOffset, anchorOffset: newSelOffset, hasFocus: true }),
      forceSelection: true,
    });

    onUpdateEditorState(editorStateWithLinkText);
    onClose();
    setTitle('');
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmLink();
    }
  }

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      css={rootCss}
      disableRestoreFocus
      onClose={onClose}
      open={open}
      slotProps={{ paper: { className: 'TE-popover' }, transition: { onExited } }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Typography align="center" variant="h6">
        Insert Tooltip
      </Typography>
      <TextField
        className="TE-textField"
        fullWidth
        inputRef={textInputRef}
        label="Display Text"
        onChange={e => setText(e.target.value)}
        onKeyPress={handleKeyPress}
        size="small"
        value={text}
      />
      <TextField
        className="TE-textField"
        fullWidth
        label="Tooltip Content"
        margin="normal"
        multiline
        onChange={e => setTitle(e.target.value)}
        onKeyPress={handleKeyPress}
        size="small"
        value={title}
      />
      <Button fullWidth onClick={confirmLink} variant="contained">
        Done
      </Button>
    </Popover>
  );
}
